import { _ as _objectWithoutProperties, d as _slicedToArray, a as _objectSpread2, c as _defineProperty, w as withTestId, u as useTheme, e as useIsNextFeatureEnabled, f as applyTestId, g as dataAttribute, m as motion$1, L as Loader } from './ProgressiveLoader-98954bba.esm.js';
import { forwardRef, useStyleConfig, chakra, useButtonGroup, Button as Button$1, Center, LightMode, DarkMode, Text, Link as Link$1 } from '@chakra-ui/react';
import { theme, mapResponsive } from '@hover/chakra-theme';
import { useCallback } from 'react';
import { fromEntries, entries, capitalize, includes, invoke } from '@hover/utilities';
import { useReducedMotion, useReducedMotionConfig, MotionConfig, AnimatePresence } from 'framer-motion';
import 'ramda/src/has';
import 'tiny-warning';
import { jsx, jsxs } from '@emotion/react/jsx-runtime';
import 'ramda/src/omit';
import 'ramda/src/mapObjIndexed';
import 'ramda/src/join';
import 'ramda/src/identity';
import 'ramda/src/ifElse';
import 'ramda/src/map';
import 'ramda/src/split';
import 'ramda/src/concat';
import 'ramda/src/toLower';
import 'ramda/src/pipe';
import { motion, colorSchemes } from '@hover/chakra-theme/foundation';
import '@emotion/react';
import '@hover/chakra-theme/bauhaus';
import '@hover/chakra-theme/next';
import 'tiny-invariant';
import 'ramda/src/mergeDeepLeft';
import 'ramda/src/mergeLeft';
import { buttonSize, textAliases } from '@hover/chakra-theme/components';
import { STROKE_ACCENT_CLASS } from '@hover/icons/constants';
import { transitions } from 'polished';
import { createContext } from '@chakra-ui/react-utils';

/**
 * Whether to disable layout animations based on user preference or
 * environment.
 *
 * Both Chakra and Framer Motion expose hooks to read the user preference from
 * CSS, but this should be the canonical hook that we use as there are other
 * cases to consider (like screenshots in Chromatic).
 */
var useIsMotionReduced = function useIsMotionReduced(override) {
  var _ref;
  var isReducedByUserPreference = useReducedMotion();

  // This reads from _both_ the CSS value, and the context value set via
  // `MotionConfig`, which allows us to set `reducedMotion="always"` in a
  // `MotionConfig` that wraps every story to disable motion in Chromatic.
  var isReducedByConfig = useReducedMotionConfig();
  return (_ref = override !== null && override !== void 0 ? override : !!isReducedByUserPreference) !== null && _ref !== void 0 ? _ref : !!isReducedByConfig;
};

var _excluded$4 = ["accent", "icon", "orientation", "role", "strokeWidth", "sx", "isInline", "isStatic"];
var Icon = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var _ref$accent = _ref.accent,
    accent = _ref$accent === void 0 ? 'currentColor' : _ref$accent,
    IconComponent = _ref.icon;
    _ref.orientation;
    var _ref$role = _ref.role,
    role = _ref$role === void 0 ? 'img' : _ref$role,
    strokeWidth = _ref.strokeWidth,
    sx = _ref.sx,
    isInline = _ref.isInline,
    isStatic = _ref.isStatic,
    props = _objectWithoutProperties(_ref, _excluded$4);
  var styles = useStyleConfig('Icon', props);

  // WTF: for some reason, this specific component was throwing hydration
  // mismatch errors when being server rendered (only in **web-react**, I
  // wasn't able to reproduce this in the documentation or demo application).
  // It turns out the issue was, for some reason, the style object properties
  // were in different orders which was producing a different checksum on the
  // client vs. the server. I think the true offender is somewhere in Chakra
  // or Emotion, but this'll do for now, pig.
  var sortedStyles = Object.fromEntries(Object.entries(styles).sort(function (_ref2, _ref3) {
    var _ref4 = _slicedToArray(_ref2, 1),
      a = _ref4[0];
    var _ref5 = _slicedToArray(_ref3, 1),
      b = _ref5[0];
    return a > b ? 1 : -1;
  }));
  var customStrokeWidth = strokeWidth ? {
    strokeWidth: strokeWidth
  } : {};
  var transition = isStatic ? {} : transitions(['color'], theme.motion.easings[250]);
  return jsx(chakra.svg, _objectSpread2(_objectSpread2(_objectSpread2({
    "aria-hidden": props.title ? 'false' : 'true',
    as: IconComponent,
    display: isInline ? 'inline' : undefined,
    margin: isInline ? 0 : undefined,
    role: role
  }, transition), withTestId(props)), {}, {
    __css: sortedStyles,
    ref: ref,
    sx: _objectSpread2(_objectSpread2(_objectSpread2({}, customStrokeWidth), sx), {}, _defineProperty({}, ".".concat(STROKE_ACCENT_CLASS), {
      stroke: accent
    }))
  }));
});
Icon.displayName = 'Icon';

var Motion = /*#__PURE__*/fromEntries( /*#__PURE__*/entries(motion.framer).map(function (_ref) {
  var _ref2 = _slicedToArray(_ref, 2),
    name = _ref2[0],
    configuration = _ref2[1];
  return [capitalize(name), function (props) {
    return jsx(MotionConfig, _objectSpread2({
      transition: configuration
    }, props));
  }];
}));

var _createContext = /*#__PURE__*/createContext({
    name: 'ColorSchemeContext',
    strict: false
  }),
  _createContext2 = /*#__PURE__*/_slicedToArray(_createContext, 3),
  Provider = _createContext2[0],
  useContext = _createContext2[1];
  _createContext2[2];
var useColorScheme = function useColorScheme() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    colorScheme = _ref.colorScheme;
  var context = useContext();
  return _objectSpread2({
    colorScheme: colorScheme
  }, context);
};
var ColorSchemeProvider = function ColorSchemeProvider(_ref2) {
  var colorScheme = _ref2.colorScheme,
    variant = _ref2.variant,
    children = _ref2.children;
  return jsx(Provider, {
    value: {
      colorScheme: typeof colorScheme === 'string' && includes(colorSchemes, colorScheme) ? colorScheme : undefined,
      variant: variant
    },
    children: children
  });
};

var getThemeHeight = function getThemeHeight(styles) {
  var _invoke = invoke(styles),
    height = _invoke.height;
  return typeof height === 'string' || typeof height === 'number' ? height : 'auto';
};

/**
 * Conditionally return props to be spread into button to make it square based
 * on the `height` defined for the specified `size` on the theme
 *
 * @param equal whether `width` and `height` should be equal
 * @param props button props
 *
 * @returns props to be spread into button
 */
var useSquareProps = function useSquareProps(equal, size) {
  // NOTE: this is an affordance for unit tests that don't properly wrap the
  // component under test in the system provider. They really all should be
  // wrapped properly, but it doesn't seem worth breaking a bunch of existing
  // tests to enforce it when they don't assert anything visual anyways.
  var theme = useTheme();
  var sizes = theme && 'components' in theme ? theme.components.Button.sizes : undefined;
  if (!equal || !sizes) return {
    size: size
  };
  var dimension = mapResponsive(function (s) {
    return includes(buttonSize, s) ? getThemeHeight(sizes[s]) : getThemeHeight(sizes["default"]);
  }, size);
  return {
    width: dimension,
    height: dimension,
    paddingX: '0 !important'
  };
};

var _excluded$3 = ["children", "color", "colorScheme", "fill", "iconAfter", "isDisabled", "isLoading", "iconBefore", "label", "onClick", "shape", "size", "variant", "disabled"];
var MotionCenter = /*#__PURE__*/motion$1(Center);
var MotionLoader = /*#__PURE__*/motion$1(Loader);
var iconSizes = {
  small: 'tiny',
  "default": 'small',
  medium: 'small',
  large: 'small'
};
var loaderSizes = {
  small: 'tiny',
  "default": 'small',
  medium: 'small',
  large: 'medium'
};
var fillToVariant = {
  solid: 'primary',
  outline: 'tertiary',
  minimal: 'ghost'
};
var transformForFill = function transformForFill(translate, transform) {
  return translate ? {
    transform: transform
  } : {};
};
var getVariants = function getVariants(translate, loader) {
  return {
    loading: _objectSpread2({
      opacity: loader ? 1 : 0
    }, transformForFill(translate, "translate3d(0, ".concat(loader ? 0 : -50, "%, 0)"))),
    idle: _objectSpread2({
      opacity: loader ? 0 : 1
    }, transformForFill(translate, "translate3d(0, ".concat(loader ? 100 : 0, "%, 0)")))
  };
};
var ForceMode = function ForceMode(_ref) {
  var colorScheme = _ref.colorScheme,
    colorMode = _ref.colorMode,
    children = _ref.children;
  var isNext = useIsNextFeatureEnabled('components');
  if (!isNext) return children;
  if (colorScheme === 'dark' || colorMode === 'light') return jsx(LightMode, {
    children: children
  });
  if (colorScheme === 'light' || colorMode === 'dark') return jsx(DarkMode, {
    children: children
  });
  return children;
};
var Button = /*#__PURE__*/forwardRef(function (_ref2, ref) {
  var _ref3, _ref4, _ref5;
  var children = _ref2.children,
    _ref2$color = _ref2.color,
    color = _ref2$color === void 0 ? 'primary' : _ref2$color,
    colorSchemeProp = _ref2.colorScheme,
    _ref2$fill = _ref2.fill,
    fill = _ref2$fill === void 0 ? 'solid' : _ref2$fill,
    iconAfter = _ref2.iconAfter,
    isDisabledProp = _ref2.isDisabled,
    isLoading = _ref2.isLoading,
    iconBefore = _ref2.iconBefore,
    label = _ref2.label,
    onClick = _ref2.onClick,
    _ref2$shape = _ref2.shape,
    shape = _ref2$shape === void 0 ? 'pill' : _ref2$shape,
    sizeProp = _ref2.size,
    variantProp = _ref2.variant,
    disabledProp = _ref2.disabled,
    props = _objectWithoutProperties(_ref2, _excluded$3);
  var group = useButtonGroup();
  var colorScheme = colorSchemeProp !== null && colorSchemeProp !== void 0 ? colorSchemeProp : group === null || group === void 0 ? void 0 : group.colorScheme;
  var variant = variantProp !== null && variantProp !== void 0 ? variantProp : group === null || group === void 0 ? void 0 : group.variant;
  var size = (_ref3 = sizeProp !== null && sizeProp !== void 0 ? sizeProp : group === null || group === void 0 ? void 0 : group.size) !== null && _ref3 !== void 0 ? _ref3 : 'default';
  var isDisabled = (_ref4 = disabledProp !== null && disabledProp !== void 0 ? disabledProp : isDisabledProp) !== null && _ref4 !== void 0 ? _ref4 : group === null || group === void 0 ? void 0 : group.isDisabled;
  var isNext = useIsNextFeatureEnabled('components');
  var isMotionReduced = useIsMotionReduced();
  var shouldSlide = fill !== 'minimal' && !isMotionReduced;

  // Apply equal width and height read from the theme for `square` and
  // `circle` button shapes. This is mostly used for `IconButton`, but it is
  // also possible to specify this shape independent of `IconButton`.
  var isSquare = shape === 'circle' || shape === 'square';
  var squareProps = useSquareProps(isSquare, size);

  // TODO: maybe we shouldn't be setting `aria-label` to the text content of
  // the button here? I'm not sure if it's actually harmful, but I'm leaving
  // it for now to avoid a potential breaking change
  var ariaLabel = (_ref5 = label !== null && label !== void 0 ? label : props['aria-label']) !== null && _ref5 !== void 0 ? _ref5 : typeof children === 'string' ? children : undefined;
  var iconSize = mapResponsive(function (s) {
    return iconSizes[s];
  }, size, 'small');
  var loaderSize = mapResponsive(function (s) {
    return loaderSizes[s];
  }, size, 'small');
  return jsx(ForceMode, {
    colorMode: props.colorMode,
    colorScheme: colorScheme !== null && colorScheme !== void 0 ? colorScheme : color,
    children: jsx(Button$1, _objectSpread2(_objectSpread2(_objectSpread2({
      "aria-label": ariaLabel,
      borderRadius: !isNext && includes(['square', 'box'], shape) ? '300' : '900',
      colorScheme: isNext ? colorScheme : color,
      "data-loading": dataAttribute(isLoading),
      isDisabled: isDisabled || isLoading,
      onClick: onClick,
      variant: isNext ? variant !== null && variant !== void 0 ? variant : fillToVariant[fill] : fill
    }, squareProps), withTestId(props)), {}, {
      ref: ref,
      children: jsxs(Motion.Tight, {
        children: [jsxs(MotionCenter, _objectSpread2(_objectSpread2({}, applyTestId({
          testId: 'wrapper'
        })), {}, {
          animate: isLoading ? 'loading' : 'idle',
          gap: "inherit",
          height: "100%",
          variants: getVariants(shouldSlide, false),
          width: "100%",
          children: [iconBefore && jsx(Icon, {
            icon: iconBefore,
            margin: "0 !important",
            size: iconSize
          }), isSquare ? children : jsx(chakra.span, {
            className: "button-label",
            display: "inline",
            children: children
          }), iconAfter && jsx(Icon, {
            icon: iconAfter,
            margin: "0 !important",
            size: iconSize
          })]
        })), jsx(AnimatePresence, {
          children: isLoading && jsx(MotionLoader, {
            animate: "loading",
            color: "inherit",
            exit: "idle",
            initial: "idle",
            position: "absolute",
            size: loaderSize,
            variants: getVariants(shouldSlide, true)
          })
        })]
      })
    }))
  });
});
Button.displayName = 'Button';

var _excluded$2 = ["icon", "iconSize", "shape", "size"];
var IconButton = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var icon = _ref.icon,
    iconSize = _ref.iconSize,
    _ref$shape = _ref.shape,
    shape = _ref$shape === void 0 ? 'circle' : _ref$shape,
    size = _ref.size,
    props = _objectWithoutProperties(_ref, _excluded$2);
  return jsx(Button, _objectSpread2(_objectSpread2({
    shape: shape,
    size: size
  }, props), {}, {
    ref: ref,
    children: jsx(Icon, {
      icon: icon,
      size: iconSize !== null && iconSize !== void 0 ? iconSize : mapResponsive(function (s) {
        return s ? iconSizes[s] : 'small';
      }, size)
    })
  }));
});
IconButton.displayName = 'IconButton';

var _excluded$1 = ["as", "children", "element", "isSemiBold", "isBold", "size", "withCapsize"];
var aliasNames = /*#__PURE__*/Object.keys(textAliases);
var getAlias = function getAlias(alias) {
  return typeof alias === 'string' && includes(aliasNames, alias) ? textAliases[alias] : alias;
};
var getSizeProps = function getSizeProps(sizeProp, withCapsize) {
  var size = mapResponsive(function (s) {
    return "".concat(getAlias(s));
  }, sizeProp);
  var textStyle = withCapsize ? mapResponsive(function (s) {
    return "body".concat(getAlias(s), "WithCapsize");
  }, sizeProp) : undefined;
  return {
    size: size,
    textStyle: textStyle
  };
};
var Body = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var as = _ref.as,
    children = _ref.children,
    element = _ref.element,
    _ref$isSemiBold = _ref.isSemiBold,
    isSemiBold = _ref$isSemiBold === void 0 ? false : _ref$isSemiBold,
    _ref$isBold = _ref.isBold,
    isBold = _ref$isBold === void 0 ? false : _ref$isBold,
    _ref$size = _ref.size,
    sizeProp = _ref$size === void 0 ? 500 : _ref$size,
    _ref$withCapsize = _ref.withCapsize,
    withCapsize = _ref$withCapsize === void 0 ? false : _ref$withCapsize,
    props = _objectWithoutProperties(_ref, _excluded$1);
  var _getSizeProps = getSizeProps(sizeProp, withCapsize),
    size = _getSizeProps.size,
    textStyle = _getSizeProps.textStyle;
  var getVariant = function getVariant() {
    if (isBold) return 'bold';
    if (isSemiBold) return 'semiBold';
    return 'default';
  };
  return jsx(Text, _objectSpread2(_objectSpread2({
    as: element !== null && element !== void 0 ? element : as,
    textStyle: textStyle
  }, withTestId(props)), {}, {
    ref: ref,
    size: size,
    variant: getVariant(),
    children: children
  }));
});
Body.displayName = 'Body';

var _excluded = ["children", "iconAfter", "iconBefore", "isMinimal", "isUnderlined", "label", "onChange", "onPress", "size", "withCapsize"];
var iconProps = {
  color: 'inherit',
  height: '1em',
  margin: 0,
  width: '1em'
};
var Link = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var _props$colorScheme;
  var children = _ref.children,
    iconAfter = _ref.iconAfter,
    iconBefore = _ref.iconBefore,
    _ref$isMinimal = _ref.isMinimal,
    isMinimal = _ref$isMinimal === void 0 ? false : _ref$isMinimal,
    _ref$isUnderlined = _ref.isUnderlined,
    isUnderlined = _ref$isUnderlined === void 0 ? false : _ref$isUnderlined,
    label = _ref.label,
    onChange = _ref.onChange,
    onPress = _ref.onPress,
    sizeProp = _ref.size,
    _ref$withCapsize = _ref.withCapsize,
    withCapsize = _ref$withCapsize === void 0 ? false : _ref$withCapsize,
    props = _objectWithoutProperties(_ref, _excluded);
  var onClick = useCallback(function (event) {
    onChange === null || onChange === void 0 || onChange(event);
    if (!props.disabled) onPress === null || onPress === void 0 || onPress(event);
  }, [onChange, props.disabled, onPress]);

  // We only want to default `size` when `Link` is rendered as a button,
  // otherwise we want it to inherit `font-size` and `line-height` by default
  var defaultedSize = props.as === 'button' && !sizeProp ? 500 : sizeProp;
  var _ref2 = defaultedSize ? getSizeProps(defaultedSize, withCapsize) : {
      size: undefined,
      textStyle: undefined
    },
    size = _ref2.size,
    textStyle = _ref2.textStyle;
  var getVariant = function getVariant() {
    if (isMinimal) return 'minimal';
    return isUnderlined ? 'persistent-underline' : 'underline';
  };
  return jsx(Link$1, _objectSpread2(_objectSpread2({
    "aria-label": label,
    onClick: onClick
  }, withTestId(props)), {}, {
    ref: ref,
    size: size,
    variant: getVariant(),
    children: jsxs(ColorSchemeProvider, {
      colorScheme: (_props$colorScheme = props.colorScheme) !== null && _props$colorScheme !== void 0 ? _props$colorScheme : 'primary',
      children: [iconBefore && jsx(Icon, _objectSpread2(_objectSpread2({}, iconProps), {}, {
        icon: iconBefore,
        marginRight: "0.25em"
      })), withCapsize ? jsx(chakra.span, {
        textStyle: textStyle,
        children: children
      }) : children, iconAfter && jsx(Icon, _objectSpread2(_objectSpread2({}, iconProps), {}, {
        icon: iconAfter,
        marginLeft: "0.25em"
      }))]
    })
  }));
});
Link.displayName = 'Link';

export { Body as B, ColorSchemeProvider as C, Icon as I, Link as L, Motion as M, Button as a, IconButton as b, useIsMotionReduced as c, getVariants as g, useColorScheme as u };
